import * as THREE from 'three';
import { InputController } from '../core/InputController';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { PlayerTank } from '../entities/PlayerTank';
import { EnemyTank } from '../entities/EnemyTank';
import { Bullet } from '../entities/Bullet';
import { ExplosionEffect } from '../systems/ExplosionEffect';
import { CollisionSystem, type PickupData } from '../systems/CollisionSystem';
import { CameraRig } from '../systems/CameraRig';
import { Hud } from '../systems/Hud';
import { AudioSystem } from '../systems/AudioSystem';
import {
  ARENA_SIZE, ARENA_HALF, ARENA_WALL_HEIGHT, ARENA_WALL_THICKNESS,
  OBSTACLE_MIN_SIZE, OBSTACLE_MAX_SIZE, OBSTACLE_MIN_HEIGHT, OBSTACLE_MAX_HEIGHT, OBSTACLE_COUNT,
  HP_PICKUP_AMOUNT, HP_PICKUP_COUNT_PER_WAVE, PICKUP_RADIUS, PICKUP_Y,
  WAVE_BASE_ENEMIES, WAVE_ENEMY_INCREMENT, WAVE_TRANSITION_TIME,
  PLAYER_LIVES, PLAYER_HP,
  COLOR_OBSTACLE, COLOR_WALL, COLOR_SKY, COLOR_PICKUP_HP,
  TANK_BODY_WIDTH, TANK_BODY_DEPTH, FIRE_COOLDOWN,
} from '../config';

type GameState = 'menu' | 'playing' | 'waveTransition' | 'gameOver';

interface ScorePopup {
  sprite: THREE.Sprite;
  velocity: THREE.Vector3;
  life: number;
}

interface MuzzleFlash {
  light: THREE.PointLight;
  life: number;
}

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(55, 1, 0.5, 200);
  private readonly input = new InputController();

  private readonly playerTank = new PlayerTank();
  private readonly enemyTanks: EnemyTank[] = [];
  private readonly playerBullets: Bullet[] = [];
  private readonly enemyBullets: Bullet[] = [];
  private readonly explosions: ExplosionEffect[] = [];
  private readonly pickups: PickupData[] = [];
  private readonly scorePopups: ScorePopup[] = [];
  private readonly muzzleFlashes: MuzzleFlash[] = [];
  private readonly obstacleBoxes: THREE.Box3[] = [];
  private readonly obstacleMeshes: THREE.Mesh[] = [];

  private readonly collision = new CollisionSystem();
  private readonly hud = new Hud();
  private readonly audio = new AudioSystem();
  private readonly cameraRig = new CameraRig(this.camera);

  private readonly loop = new Loop(
    (delta, elapsed) => this.update(delta, elapsed),
    () => this.render(),
  );

  private state: GameState = 'menu';
  private score = 0;
  private currentWave = 1;
  private lives = PLAYER_LIVES;
  private waveTransitionTimer = 0;
  private frame = 0;
  private elapsedTime = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.renderer.toneMappingExposure = 1.0;
    this.createScene();
    this.hud.showMenu();

    document.getElementById('start-button')!.addEventListener('click', () => this.startGame());
    document.getElementById('restart-button')!.addEventListener('click', () => this.restartGame());
    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyR' && this.state === 'gameOver') this.restartGame();
    });
    resizeRenderer(this.renderer, this.camera);
  }

  start(): void { this.loop.start(); this.publishDiagnostics(); }

  dispose(): void {
    this.loop.stop(); this.input.dispose(); this.audio.dispose(); this.cleanupScene(); this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
  }

  // ==================== 场景构建 ====================

  private createScene(): void {
    this.scene.background = new THREE.Color(COLOR_SKY);
    this.scene.fog = new THREE.Fog(COLOR_SKY, 30, 80);

    const ambient = new THREE.AmbientLight('#8899aa', 0.6);
    this.scene.add(ambient);
    const hemisphere = new THREE.HemisphereLight('#aaccff', '#334422', 0.8);
    this.scene.add(hemisphere);

    const sun = new THREE.DirectionalLight('#fff8e7', 1.8);
    sun.position.set(20, 25, 15);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 1; sun.shadow.camera.far = 80;
    sun.shadow.camera.left = -40; sun.shadow.camera.right = 40;
    sun.shadow.camera.top = 40; sun.shadow.camera.bottom = -40;
    this.scene.add(sun);

    this.createArena();
    this.createObstacles();
    this.playerTank.group.position.set(5, 0, 5);
    this.scene.add(this.playerTank.group);
  }

  private createArena(): void {
    // 棋盘格纹理地面
    const groundCanvas = document.createElement('canvas');
    groundCanvas.width = 512; groundCanvas.height = 512;
    const ctx = groundCanvas.getContext('2d')!;
    const tileSize = 32;
    for (let y = 0; y < 512; y += tileSize) {
      for (let x = 0; x < 512; x += tileSize) {
        ctx.fillStyle = ((x / tileSize + y / tileSize) % 2 === 0) ? '#4a5742' : '#3d4a3a';
        ctx.fillRect(x, y, tileSize, tileSize);
      }
    }
    const groundTex = new THREE.CanvasTexture(groundCanvas);
    groundTex.wrapS = THREE.RepeatWrapping; groundTex.wrapT = THREE.RepeatWrapping;
    groundTex.repeat.set(4, 4);
    groundTex.colorSpace = THREE.SRGBColorSpace;

    const groundGeo = new THREE.PlaneGeometry(ARENA_SIZE, ARENA_SIZE);
    const groundMat = new THREE.MeshStandardMaterial({
      map: groundTex,
      roughness: 0.78, metalness: 0.05,
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.scene.add(ground);

    // 网格线
    const gridHelper = new THREE.PolarGridHelper(ARENA_HALF, 30, 15, 64, '#666', '#555');
    gridHelper.position.y = 0.01;
    this.scene.add(gridHelper);

    // 墙壁
    const wallMat = new THREE.MeshStandardMaterial({ color: COLOR_WALL, roughness: 0.7, metalness: 0.2 });
    const wallGeoLong = new THREE.BoxGeometry(ARENA_SIZE + ARENA_WALL_THICKNESS * 2, ARENA_WALL_HEIGHT, ARENA_WALL_THICKNESS);
    const wallGeoShort = new THREE.BoxGeometry(ARENA_WALL_THICKNESS, ARENA_WALL_HEIGHT, ARENA_SIZE);
    const walls = [
      [wallGeoLong, 0, ARENA_WALL_HEIGHT / 2, -ARENA_HALF - ARENA_WALL_THICKNESS / 2],
      [wallGeoLong, 0, ARENA_WALL_HEIGHT / 2, ARENA_HALF + ARENA_WALL_THICKNESS / 2],
      [wallGeoShort, -ARENA_HALF - ARENA_WALL_THICKNESS / 2, ARENA_WALL_HEIGHT / 2, 0],
      [wallGeoShort, ARENA_HALF + ARENA_WALL_THICKNESS / 2, ARENA_WALL_HEIGHT / 2, 0],
    ];
    for (const [geo, x, y, z] of walls) {
      const mesh = new THREE.Mesh(geo as THREE.BoxGeometry, wallMat);
      mesh.position.set(x as number, y as number, z as number);
      mesh.castShadow = true; mesh.receiveShadow = true;
      this.scene.add(mesh);
    }
  }

  private createObstacles(): void {
    const mat = new THREE.MeshStandardMaterial({ color: COLOR_OBSTACLE, roughness: 0.65, metalness: 0.15 });
    const placements: { x: number; z: number; w: number; d: number; h: number }[] = [];
    const usedAreas: { x: number; z: number; r: number }[] = [{ x: 5, z: 5, r: 5 }];

    let attempts = 0;
    while (placements.length < OBSTACLE_COUNT && attempts < 200) {
      attempts++;
      const w = OBSTACLE_MIN_SIZE + Math.random() * (OBSTACLE_MAX_SIZE - OBSTACLE_MIN_SIZE);
      const d = OBSTACLE_MIN_SIZE + Math.random() * (OBSTACLE_MAX_SIZE - OBSTACLE_MIN_SIZE);
      const h = OBSTACLE_MIN_HEIGHT + Math.random() * (OBSTACLE_MAX_HEIGHT - OBSTACLE_MIN_HEIGHT);
      const margin = 4;
      const x = (Math.random() - 0.5) * (ARENA_SIZE - margin * 2);
      const z = (Math.random() - 0.5) * (ARENA_SIZE - margin * 2);
      let overlaps = false;
      for (const area of usedAreas) {
        const dx = x - area.x; const dz = z - area.z;
        if (Math.sqrt(dx * dx + dz * dz) < area.r + Math.max(w, d) / 2 + 2) { overlaps = true; break; }
      }
      if (!overlaps) {
        placements.push({ x, z, w, d, h });
        usedAreas.push({ x, z, r: Math.max(w, d) / 2 + 2 });
      }
    }
    for (const p of placements) {
      const geo = new THREE.BoxGeometry(p.w, p.h, p.d);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(p.x, p.h / 2, p.z);
      mesh.castShadow = true; mesh.receiveShadow = true;
      this.scene.add(mesh);
      this.obstacleMeshes.push(mesh);
      this.obstacleBoxes.push(new THREE.Box3().setFromObject(mesh));
    }
  }

  // ==================== 道具 ====================

  private spawnPickups(): void {
    for (const p of this.pickups) {
      this.scene.remove(p.mesh);
      p.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
          else child.material.dispose();
        }
      });
    }
    this.pickups.length = 0;

    for (let i = 0; i < HP_PICKUP_COUNT_PER_WAVE; i++) {
      const x = (Math.random() - 0.5) * (ARENA_SIZE - 8);
      const z = (Math.random() - 0.5) * (ARENA_SIZE - 8);
      // 十字形血包
      const group = new THREE.Group();
      const barGeo = new THREE.BoxGeometry(PICKUP_RADIUS * 0.5, 0.2, PICKUP_RADIUS * 1.6);
      const barGeo2 = new THREE.BoxGeometry(PICKUP_RADIUS * 1.6, 0.2, PICKUP_RADIUS * 0.5);
      const pickMat = new THREE.MeshStandardMaterial({
        color: COLOR_PICKUP_HP, roughness: 0.3, metalness: 0.1,
        emissive: COLOR_PICKUP_HP, emissiveIntensity: 0.5,
      });
      const bar1 = new THREE.Mesh(barGeo, pickMat);
      const bar2 = new THREE.Mesh(barGeo2, pickMat);
      group.add(bar1); group.add(bar2);
      group.position.set(x, PICKUP_Y, z);
      this.scene.add(group);

      this.pickups.push({
        position: new THREE.Vector3(x, PICKUP_Y, z),
        radius: PICKUP_RADIUS,
        collected: false,
        mesh: group,
      });
    }
  }

  // ==================== 游戏流程 ====================

  private startGame(): void {
    this.audio.init();
    this.score = 0; this.currentWave = 1; this.lives = PLAYER_LIVES; this.state = 'playing';
    this.playerTank.reset();
    for (const e of this.enemyTanks) { this.scene.remove(e.group); e.dispose(); }
    this.enemyTanks.length = 0;
    this.playerBullets.length = 0; this.enemyBullets.length = 0;
    this.hud.hideMenu(); this.hud.hideGameOver();
    this.hud.updateScore(0); this.hud.updateLives(this.lives);
    this.spawnPickups(); this.spawnWave();
    this.cameraRig.snapTo(this.playerTank.group.position, this.playerTank.group.rotation.y);
    this.audio.startMusic();
  }

  private restartGame(): void { this.startGame(); }

  private spawnWave(): void {
    const enemyCount = WAVE_BASE_ENEMIES + (this.currentWave - 1) * WAVE_ENEMY_INCREMENT;
    for (const e of this.enemyTanks) { this.scene.remove(e.group); e.dispose(); }
    this.enemyTanks.length = 0;

    for (let i = 0; i < enemyCount; i++) {
      const enemy = new EnemyTank(this.currentWave);
      let x: number, z: number;
      do {
        x = (Math.random() - 0.5) * (ARENA_SIZE - 8);
        z = (Math.random() - 0.5) * (ARENA_SIZE - 8);
      } while (this.playerTank.group.position.distanceTo(new THREE.Vector3(x, 0, z)) < 12);
      enemy.spawnAt(x, z);
      this.scene.add(enemy.group);
      this.enemyTanks.push(enemy);
    }
    this.hud.updateWave(this.currentWave);
    this.hud.updateEnemyCount(enemyCount);
    if (this.currentWave > 1) { this.hud.showWaveStart(this.currentWave); this.spawnPickups(); }
  }

  // ==================== 主更新循环 ====================

  private update(delta: number, elapsed: number): void {
    this.frame++;
    this.elapsedTime = elapsed;
    resizeRenderer(this.renderer, this.camera);

    switch (this.state) {
      case 'menu':
        if (this.input.consumeKey('Space')) this.startGame();
        this.camera.position.set(Math.sin(elapsed * 0.2) * 18, 14, Math.cos(elapsed * 0.2) * 18);
        this.camera.lookAt(0, 0, 0);
        break;
      case 'playing':
        this.updatePlaying(delta);
        break;
      case 'waveTransition':
        this.waveTransitionTimer -= delta;
        if (this.waveTransitionTimer <= 0) { this.currentWave++; this.spawnWave(); this.state = 'playing'; }
        break;
      case 'gameOver':
        break;
    }
    this.publishDiagnostics();
  }

  private updatePlaying(delta: number): void {
    const tankInput = this.input.readInput();

    // 玩家
    const prevPB = this.playerBullets.length;
    this.playerTank.update(delta, tankInput, this.playerBullets);
    this.addNewBulletsToScene(this.playerBullets, prevPB);
    if (this.playerBullets.length > prevPB) { this.createMuzzleFlash(this.playerTank); this.audio.playPlayerFire(); }

    const pushOut = this.collision.resolveTankObstacle(
      this.playerTank.group.position, TANK_BODY_WIDTH / 2, TANK_BODY_DEPTH / 2, this.obstacleBoxes,
    );
    this.playerTank.group.position.add(pushOut);
    this.playerTank.clampToArena();

    // 敌人
    for (const enemy of this.enemyTanks) {
      const prevEB = this.enemyBullets.length;
      enemy.update(delta, this.playerTank.group.position, this.enemyBullets, this.obstacleBoxes);
      this.addNewBulletsToScene(this.enemyBullets, prevEB);
      if (this.enemyBullets.length > prevEB) { this.createMuzzleFlash(enemy); this.audio.playEnemyFire(); }

      const ep = this.collision.resolveTankObstacle(
        enemy.group.position, TANK_BODY_WIDTH / 2, TANK_BODY_DEPTH / 2, this.obstacleBoxes,
      );
      enemy.group.position.add(ep);
    }

    // 炮弹
    for (const b of this.playerBullets) b.update(delta);
    for (const b of this.enemyBullets) b.update(delta);

    // 碰撞
    const collisionResult = this.collision.checkBulletTankCollisions(
      [...this.playerBullets, ...this.enemyBullets],
      this.playerTank, this.enemyTanks, this.obstacleBoxes,
    );

    for (const enemy of collisionResult.killedEnemies) {
      this.score += 100;
      this.hud.updateScore(this.score);
      this.createScorePopup(enemy.group.position.clone(), '+100');
      const explosion = new ExplosionEffect(enemy.group.position.clone(), '#ff6b35', this.scene);
      this.scene.add(explosion.group);
      this.explosions.push(explosion);
      this.cameraRig.shake(0.8, 0.15);
      this.audio.playExplosion();
      this.scene.remove(enemy.group);
      enemy.dispose();
      const idx = this.enemyTanks.indexOf(enemy);
      if (idx >= 0) this.enemyTanks.splice(idx, 1);
    }

    if (collisionResult.hitPlayer) {
      this.audio.playPlayerHit();
      const died = this.playerTank.takeDamage(collisionResult.playerDamage);
      this.hud.updateHp(this.playerTank.hp);
      if (died) {
        this.lives--;
        this.hud.updateLives(this.lives);
        const explosion = new ExplosionEffect(this.playerTank.group.position.clone(), '#ff4444', this.scene);
        this.scene.add(explosion.group);
        this.explosions.push(explosion);
        this.cameraRig.shake(1.2, 0.3);
        if (this.lives <= 0) { this.state = 'gameOver'; this.hud.showGameOver(this.score, this.currentWave); this.audio.playGameOver(); this.audio.stopMusic(); return; }
        this.playerTank.reset();
        this.playerTank.group.position.set(5, 0, 5);
        this.hud.updateHp(this.playerTank.hp);
        this.cameraRig.snapTo(this.playerTank.group.position, this.playerTank.group.rotation.y);
      }
    }

    this.collectDeadBullets(this.playerBullets);
    this.collectDeadBullets(this.enemyBullets);

    // 道具
    const collected = this.collision.checkPickups(this.playerTank.group.position, this.pickups, TANK_BODY_WIDTH / 2);
    for (const pickup of collected) {
      this.playerTank.hp = Math.min(PLAYER_HP, this.playerTank.hp + HP_PICKUP_AMOUNT);
      this.hud.updateHp(this.playerTank.hp);
      pickup.mesh.visible = false;
      this.createScorePopup(pickup.position.clone(), `+${HP_PICKUP_AMOUNT}HP`);
      this.audio.playPickup();
    }

    // 道具动画（浮动+旋转）
    for (const pickup of this.pickups) {
      if (!pickup.collected) {
        pickup.mesh.position.y = pickup.position.y + Math.sin(this.elapsedTime * 3 + pickup.position.x) * 0.3;
        pickup.mesh.rotation.y += delta * 1.5;
      }
    }

    // 爆炸效果
    for (const exp of this.explosions) exp.update(delta);
    this.collectDeadExplosions();

    // 得分飘字
    for (const sp of this.scorePopups) {
      sp.life -= delta;
      sp.sprite.position.addScaledVector(sp.velocity, delta);
      sp.sprite.material.opacity = Math.max(0, sp.life / 1.0);
      sp.sprite.position.y += delta * 2;
    }
    this.collectDeadScorePopups();

    // 炮口闪光
    for (const mf of this.muzzleFlashes) {
      mf.life -= delta;
      mf.light.intensity = Math.max(0, mf.life * 30);
    }
    this.collectDeadMuzzleFlashes();

    // 波次
    const aliveEnemies = this.enemyTanks.filter(e => e.alive).length;
    this.hud.updateEnemyCount(aliveEnemies);
    if (aliveEnemies === 0) {
      this.hud.showWaveComplete(this.currentWave);
      this.audio.playWaveComplete();
      this.state = 'waveTransition';
      this.waveTransitionTimer = WAVE_TRANSITION_TIME;
    }

    this.cameraRig.update(delta, this.playerTank.group.position, this.playerTank.group.rotation.y);
    this.hud.updateCooldown(1 - this.playerTank.cooldownTimer / FIRE_COOLDOWN);
  }

  // ==================== 特效 ====================

  private createMuzzleFlash(tank: PlayerTank | EnemyTank): void {
    const pos = new THREE.Vector3();
    tank.getMuzzleWorldPos(pos);
    const light = new THREE.PointLight('#ffaa44', 4, 6, 2);
    light.position.copy(pos);
    this.scene.add(light);
    this.muzzleFlashes.push({ light, life: 0.08 });
  }

  private createScorePopup(position: THREE.Vector3, text: string): void {
    const canvas = document.createElement('canvas');
    canvas.width = 128; canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    ctx.font = 'bold 28px "Segoe UI", sans-serif';
    ctx.fillStyle = '#f5ba49';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3;
    ctx.textAlign = 'center';
    ctx.strokeText(text, 64, 38);
    ctx.fillText(text, 64, 38);

    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearFilter;
    tex.colorSpace = THREE.SRGBColorSpace;
    const spriteMat = new THREE.SpriteMaterial({ map: tex, transparent: true, opacity: 1, depthTest: false });
    const sprite = new THREE.Sprite(spriteMat);
    sprite.position.copy(position).add(new THREE.Vector3(0, 3, 0));
    sprite.scale.set(4, 2, 1);
    this.scene.add(sprite);
    this.scorePopups.push({ sprite, velocity: new THREE.Vector3(0, 1, 0), life: 1.0 });
  }

  // ==================== 清理 ====================

  private addNewBulletsToScene(bullets: Bullet[], prevCount: number): void {
    for (let i = prevCount; i < bullets.length; i++) {
      this.scene.add(bullets[i].mesh);
      this.scene.add(bullets[i].trail);
    }
  }

  private collectDeadBullets(bullets: Bullet[]): void {
    for (let i = bullets.length - 1; i >= 0; i--) {
      if (!bullets[i].alive) {
        const b = bullets[i];
        this.scene.remove(b.mesh); this.scene.remove(b.trail);
        b.dispose(); bullets.splice(i, 1);
      }
    }
  }

  private collectDeadExplosions(): void {
    for (let i = this.explosions.length - 1; i >= 0; i--) {
      if (!this.explosions[i].alive) {
        const exp = this.explosions[i];
        this.scene.remove(exp.group);
        if (exp.light) this.scene.remove(exp.light);
        exp.dispose();
        this.explosions.splice(i, 1);
      }
    }
  }

  private collectDeadScorePopups(): void {
    for (let i = this.scorePopups.length - 1; i >= 0; i--) {
      if (this.scorePopups[i].life <= 0) {
        const sp = this.scorePopups[i];
        this.scene.remove(sp.sprite);
        sp.sprite.material.map?.dispose();
        sp.sprite.material.dispose();
        this.scorePopups.splice(i, 1);
      }
    }
  }

  private collectDeadMuzzleFlashes(): void {
    for (let i = this.muzzleFlashes.length - 1; i >= 0; i--) {
      if (this.muzzleFlashes[i].life <= 0) {
        this.scene.remove(this.muzzleFlashes[i].light);
        this.muzzleFlashes.splice(i, 1);
      }
    }
  }

  private cleanupScene(): void {
    this.audio.stopMusic();
    for (const b of this.playerBullets) { this.scene.remove(b.mesh); this.scene.remove(b.trail); b.dispose(); }
    for (const b of this.enemyBullets) { this.scene.remove(b.mesh); this.scene.remove(b.trail); b.dispose(); }
    for (const e of this.enemyTanks) { this.scene.remove(e.group); e.dispose(); }
    for (const exp of this.explosions) { this.scene.remove(exp.group); if (exp.light) this.scene.remove(exp.light); exp.dispose(); }
    for (const p of this.pickups) { 
      this.scene.remove(p.mesh); 
      p.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
          else child.material.dispose();
        }
      });
    }
    for (const sp of this.scorePopups) { this.scene.remove(sp.sprite); sp.sprite.material.map?.dispose(); sp.sprite.material.dispose(); }
    for (const mf of this.muzzleFlashes) { this.scene.remove(mf.light); }
    for (const obs of this.obstacleMeshes) { obs.geometry.dispose(); (obs.material as THREE.Material).dispose(); }
    this.playerTank.dispose();
    this.playerBullets.length = 0; this.enemyBullets.length = 0; this.enemyTanks.length = 0;
    this.explosions.length = 0; this.pickups.length = 0; this.scorePopups.length = 0;
    this.muzzleFlashes.length = 0; this.obstacleMeshes.length = 0; this.obstacleBoxes.length = 0;
  }

  private render(): void { this.renderer.render(this.scene, this.camera); }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame, state: this.state, score: this.score, wave: this.currentWave, lives: this.lives,
      player: { position: { x: this.playerTank.group.position.x, y: this.playerTank.group.position.y, z: this.playerTank.group.position.z }, speed: 0, hp: this.playerTank.hp },
      enemies: this.enemyTanks.filter(e => e.alive).length,
      bullets: this.playerBullets.length + this.enemyBullets.length,
      explosions: this.explosions.length,
      renderer: { calls: info.render.calls, triangles: info.render.triangles, geometries: info.memory.geometries, textures: info.memory.textures },
    };
  }
}
